import { useMemo } from "react";

import { ProtectedLink } from "@impero-com/common/components/protectedLink/index";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    TST_BLESSED_ID_COLUMN_SELECTION,
    TST_COLUMN_SIZE,
    TST_DEFAULT_PAGE_SIZE,
} from "@impero-com/ui";
import {
    FilterTarget,
    usePresetTableSearchParams,
    ZodCodecMapToType,
} from "@impero-com/ui/domain";
import * as Sentry from "@sentry/react";
import {
    createColumnHelper,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
    getFacetedUniqueValues,
    getPaginationRowModel,
    Column,
} from "@tanstack/react-table";
import { Checkbox } from "antd";

import {
    StandardAccessRight,
    ControlProgramForUserListDto,
} from "api/generated/model";
import { CustomEllipsisLabel } from "components/table/tsTable/CustomEllipsisLabel";
import { TSTValidMultipleOptionFilterSelect } from "components/table/tsTable/TSTValidMultipleOptionFilterSelect";
import { TSTValidTagsSelectFilter } from "components/table/tsTable/TSTValidTagsSelectFilter";
import { Tag, TagCategory } from "components/tag/types";
import { buildControlProgramViewUrl } from "org/control/controlProgram/urlUtils";
import {
    formatTagsAndCategoriesString,
    tagAndCategoryListDefaultTSTCell,
    tagsTSTFilterFn,
    tagsTSTSortingFn,
} from "org/tags/tagsTSTUtils";
import { createFilterFnWithMode } from "utils/table/createFilterFnWithMode";
import { createFilterHelpersGetter } from "utils/table/getTypedFilterValue";
import { tstArrIncludesSomeFilterFn } from "utils/table/tstArrIncludesSomeFilterFn";

import {
    CONTROL_PROGRAM_LIST_COLUMN_IDS,
    CONTROL_PROGRAM_TABLE_STATE_STORAGE_KEY,
    controlProgramListColumnFiltersCodecMap,
    controlProgramListTableDefaultValues,
} from "./consts";

export interface ControlProgramRow {
    id: number;
    name: string;
    canView: boolean;
    accessRight: StandardAccessRight;
    status: string;
    running: boolean;
    isSoleAdmin: boolean;
    tagIds: string[];
}

type ControlProgramStatus = "Active" | "Inactive";

function statusKey(running: boolean): ControlProgramStatus {
    return running ? "Active" : "Inactive";
}

function localizeStatus(t: TFunction, status: ControlProgramStatus): string {
    return status === "Active"
        ? t("ViewControlProgram_Status_Active")
        : t("ViewControlProgram_Status_Inactive");
}

function localizeAccessRight(
    t: TFunction,
    accessRight: StandardAccessRight
): string {
    switch (accessRight) {
        case "Admin":
            return t("AccessRights_Admin");
        case "Edit":
            return t("AccessRights_Edit");
        case "View":
            return t("AccessRights_View");
        default:
            return assertNever(accessRight);
    }
}

function adaptTags(data: ControlProgramForUserListDto): Tag[] {
    return data.linkedResources.tags.map((tag) => ({
        id: tag.id,
        name: tag.name,
        tagCategoryId: tag.controlProgramTagCategoryId,
    }));
}

function adaptTagCategories(data: ControlProgramForUserListDto): TagCategory[] {
    return data.linkedResources.tagCategories.map((category) => ({
        id: category.id,
        name: category.name,
    }));
}

function buildRows(
    t: TFunction,
    data: ControlProgramForUserListDto
): ControlProgramRow[] {
    return data.content.map(({ resource, actions }) => ({
        id: resource.controlProgram.id,
        name: resource.controlProgram.name,
        canView: actions.view,
        accessRight: resource.accessRight,
        status: localizeStatus(t, statusKey(resource.controlProgram.running)),
        running: resource.controlProgram.running,
        isSoleAdmin: resource.isSoleAdmin,
        tagIds: resource.tagIds,
    }));
}

export function useUserControlProgramListTable(
    data: ControlProgramForUserListDto
) {
    const { t } = useTranslation();

    const tableData = useMemo(() => buildRows(t, data), [t, data]);
    const notificationLabel = t("Table_RemovedFiltersWarning");

    const stateAndOnChanges = usePresetTableSearchParams(
        CONTROL_PROGRAM_TABLE_STATE_STORAGE_KEY,
        controlProgramListTableDefaultValues,
        controlProgramListColumnFiltersCodecMap,
        notificationLabel,
        Sentry.captureException
    );
    const columns = buildColumns(t, data);
    const table = useReactTable({
        ...stateAndOnChanges,
        data: tableData,
        columns,
        getRowId: (row) => row.id.toString(),
        initialState: {
            ...stateAndOnChanges.initialState,
            pagination: {
                pageSize: TST_DEFAULT_PAGE_SIZE,
                pageIndex: 0,
            },
        },
        enableSortingRemoval: false,
        getCoreRowModel: getCoreRowModel<ControlProgramRow>(),
        getSortedRowModel: getSortedRowModel<ControlProgramRow>(),
        getFilteredRowModel: getFilteredRowModel<ControlProgramRow>(),
        getFacetedMinMaxValues: getFacetedMinMaxValues<ControlProgramRow>(),
        getFacetedUniqueValues: getFacetedUniqueValues<ControlProgramRow>(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const filterComponent = (
        column: Column<ControlProgramRow>,
        target: FilterTarget
    ) => getFilterComponent(t, column, target, data);

    return {
        table,
        getFilterComponent: filterComponent,
        canAddException: (column: Column<ControlProgramRow>) =>
            column.id === CONTROL_PROGRAM_LIST_COLUMN_IDS.TAGS,
        tableId: CONTROL_PROGRAM_TABLE_STATE_STORAGE_KEY,
    };
}

const columnHelper = createColumnHelper<ControlProgramRow>();
function buildColumns(t: TFunction, data: ControlProgramForUserListDto) {
    const tags = adaptTags(data);
    const tagCategories = adaptTagCategories(data);
    return [
        columnHelper.display({
            id: TST_BLESSED_ID_COLUMN_SELECTION,
            header: (info) => (
                <Checkbox
                    checked={info.table.getIsAllPageRowsSelected()}
                    indeterminate={info.table.getIsSomePageRowsSelected()}
                    onChange={info.table.getToggleAllRowsSelectedHandler()}
                />
            ),
            size: TST_COLUMN_SIZE.CHECKBOX,
            meta: { fixedWidth: true },
            cell: (info) => (
                <Checkbox
                    checked={info.row.getIsSelected()}
                    onClick={() => info.row.toggleSelected()}
                />
            ),
        }),
        columnHelper.accessor((row) => row.name, {
            id: CONTROL_PROGRAM_LIST_COLUMN_IDS.CONTROL_PROGRAM,
            cell: (info) => {
                const row = info.row.original;
                return (
                    <ProtectedLink
                        href={buildControlProgramViewUrl(row.id)}
                        hasAccess={row.canView}
                    >
                        {info.getValue()}
                    </ProtectedLink>
                );
            },
            enableSorting: true,
            sortingFn: "alphanumericCaseSensitive",
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<ControlProgramRow, string[]>(
                (row, value) =>
                    tstArrIncludesSomeFilterFn(
                        row,
                        CONTROL_PROGRAM_LIST_COLUMN_IDS.CONTROL_PROGRAM,
                        value
                    )
            ),
            header: () =>
                t("AccessAndAssignment_ControlPrograms_ControlProgram"),
            size: TST_COLUMN_SIZE.XL,
        }),
        columnHelper.accessor(
            (row) => localizeAccessRight(t, row.accessRight),
            {
                id: CONTROL_PROGRAM_LIST_COLUMN_IDS.ACCESS_RIGHT,
                cell: (info) => <span>{info.getValue()}</span>,
                enableSorting: true,
                sortingFn: "alphanumeric",
                enableColumnFilter: true,
                filterFn: createFilterFnWithMode<ControlProgramRow, string[]>(
                    (row, value) => value.includes(row.original.accessRight)
                ),
                header: () =>
                    t("AccessAndAssignment_ControlPrograms_AccessRight"),
                size: TST_COLUMN_SIZE.S,
            }
        ),
        columnHelper.accessor((row) => row.status, {
            id: CONTROL_PROGRAM_LIST_COLUMN_IDS.STATUS,
            cell: (info) => <span>{info.getValue()}</span>,
            enableSorting: true,
            sortingFn: "alphanumeric",
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<ControlProgramRow, string[]>(
                (row, value) => value.includes(statusKey(row.original.running))
            ),
            header: () => t("AccessAndAssignment_ControlPrograms_Status"),
            size: TST_COLUMN_SIZE.S,
        }),
        columnHelper.accessor(
            (row) =>
                formatTagsAndCategoriesString(row.tagIds, {
                    tags,
                    tagCategories,
                }),
            {
                id: CONTROL_PROGRAM_LIST_COLUMN_IDS.TAGS,
                cell: (info) =>
                    tagAndCategoryListDefaultTSTCell(
                        info.row.original.tagIds,
                        tags,
                        tagCategories
                    ),
                enableSorting: true,
                sortingFn: (rowA, rowB) =>
                    tagsTSTSortingFn(
                        rowA.original.tagIds,
                        rowB.original.tagIds
                    ),
                enableColumnFilter: true,
                filterFn: createFilterFnWithMode<ControlProgramRow, string[]>(
                    (row, value) => tagsTSTFilterFn(row.original.tagIds, value)
                ),
                header: () => t("Tags"),
                size: TST_COLUMN_SIZE.XL,
            }
        ),
    ];
}

type ControlProgramListColumnFilters = ZodCodecMapToType<
    typeof controlProgramListColumnFiltersCodecMap
>;

type ControlProgramFilterableColumnId = keyof ControlProgramListColumnFilters;

function getUniqueTagIds(column: Column<ControlProgramRow>): string[] {
    const tagIdsPerRow = column
        .getFacetedRowModel()
        .flatRows.map((row) => row.original.tagIds);
    return Array.from(new Set(tagIdsPerRow.flat()));
}

function getFilterComponent(
    t: TFunction,
    column: Column<ControlProgramRow>,
    target: FilterTarget,
    data: ControlProgramForUserListDto
) {
    const getFilterHelpers =
        createFilterHelpersGetter<ControlProgramListColumnFilters>(column);
    const columnId = column.id as ControlProgramFilterableColumnId;

    switch (columnId) {
        case CONTROL_PROGRAM_LIST_COLUMN_IDS.CONTROL_PROGRAM: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const values: Map<string, number> = column.getFacetedUniqueValues();
            const options = [...values.keys()].map((option) => ({
                label: option,
                value: option,
                customLabel: <CustomEllipsisLabel text={option} />,
            }));
            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={options}
                    placeholder={t(
                        "AccessAndAssignment_ControlPrograms_ControlProgram"
                    )}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case CONTROL_PROGRAM_LIST_COLUMN_IDS.ACCESS_RIGHT: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const accessRights = new Set(
                column
                    .getFacetedRowModel()
                    .flatRows.map((row) => row.original.accessRight)
            );
            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={[...accessRights].map((accessRight) => ({
                        label: localizeAccessRight(t, accessRight),
                        value: accessRight,
                    }))}
                    placeholder={t(
                        "AccessAndAssignment_ControlPrograms_AccessRight"
                    )}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case CONTROL_PROGRAM_LIST_COLUMN_IDS.STATUS: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const statuses = new Set(
                column
                    .getFacetedRowModel()
                    .flatRows.map((row) => statusKey(row.original.running))
            );
            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={[...statuses].map((status) => ({
                        label: localizeStatus(t, status),
                        value: status,
                    }))}
                    placeholder={t(
                        "AccessAndAssignment_ControlPrograms_Status"
                    )}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case CONTROL_PROGRAM_LIST_COLUMN_IDS.TAGS: {
            const { value, setFilterValue } = getFilterHelpers(
                columnId,
                target
            );
            return (
                <TSTValidTagsSelectFilter
                    tagCategories={adaptTagCategories(data)}
                    tags={adaptTags(data)}
                    uniqueTagIds={getUniqueTagIds(column)}
                    placeholder={t("Tags")}
                    setFilterValue={setFilterValue}
                    value={value}
                />
            );
        }
        default:
            return assertNever(columnId);
    }
}
